import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Search, Filter, Shield, Home, Briefcase,
  ChevronLeft, ChevronRight, Clock, TrendingUp, RefreshCw,
  Mail, Calendar, MoreVertical, UserCog, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await fetch(`${API}/api/admin/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load user stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString()
      });
      
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      if (search) params.append('search', search);
      if (timeFilter && timeFilter !== 'all') params.append('time_filter', timeFilter);
      
      const response = await fetch(`${API}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setUsers(data.users);
      setTotal(data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [token, page, roleFilter, timeFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });
      
      if (!response.ok) throw new Error('Failed to update role');
      
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-700 border-purple-200',
      sponsor: 'bg-blue-100 text-blue-700 border-blue-200',
      homeowner: 'bg-green-100 text-green-700 border-green-200'
    };
    const icons = {
      admin: <Shield className="w-3 h-3 mr-1" />,
      sponsor: <Briefcase className="w-3 h-3 mr-1" />,
      homeowner: <Home className="w-3 h-3 mr-1" />
    };
    return (
      <Badge variant="outline" className={`${styles[role] || 'bg-gray-100'} flex items-center`}>
        {icons[role]}
        {role}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isNewUser = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return date > dayAgo;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
            User Management
          </h1>
          <p className="text-neutral-500 mt-1">Monitor signups and manage user roles</p>
        </div>
        <Button 
          onClick={() => { fetchUsers(); fetchStats(); }}
          variant="outline"
          className="gap-2"
          data-testid="refresh-users-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Total Users</p>
              <p className="text-2xl font-bold text-neutral-900 mt-1">
                {statsLoading ? '...' : stats?.total_users?.toLocaleString()}
              </p>
            </div>
            <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-neutral-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">New Today</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {statsLoading ? '...' : `+${stats?.new_today || 0}`}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">This Week</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {statsLoading ? '...' : `+${stats?.new_this_week || 0}`}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">This Month</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {statsLoading ? '...' : `+${stats?.new_this_month || 0}`}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Role Breakdown */}
      {stats && (
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <h3 className="font-semibold text-neutral-900 mb-4">Users by Role</h3>
          <div className="flex gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm text-neutral-600">Homeowners:</span>
              <span className="font-semibold">{stats.by_role?.homeowners || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-sm text-neutral-600">Sponsors:</span>
              <span className="font-semibold">{stats.by_role?.sponsors || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="text-sm text-neutral-600">Admins:</span>
              <span className="font-semibold">{stats.by_role?.admins || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
          
          <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
            <SelectTrigger className="w-full md:w-[160px]" data-testid="role-filter">
              <Filter className="w-4 h-4 mr-2 text-neutral-400" />
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="homeowner">Homeowners</SelectItem>
              <SelectItem value="sponsor">Sponsors</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>

          <Select value={timeFilter} onValueChange={(v) => { setTimeFilter(v); setPage(0); }}>
            <SelectTrigger className="w-full md:w-[160px]" data-testid="time-filter">
              <Clock className="w-4 h-4 mr-2 text-neutral-400" />
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Last 24 Hours</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-600">User</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-600">Email</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-600">Role</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-600">Joined</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-neutral-500">
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Loading users...
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-neutral-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr 
                    key={user.user_id} 
                    className={`hover:bg-neutral-50 transition-colors ${isNewUser(user.created_at) ? 'bg-green-50/50' : ''}`}
                    data-testid={`user-row-${user.user_id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF3B30] to-[#FF6B6B] flex items-center justify-center text-white font-semibold">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900 flex items-center gap-2">
                            {user.name || 'Unnamed'}
                            {isNewUser(user.created_at) && (
                              <Badge className="bg-green-500 text-white text-xs">NEW</Badge>
                            )}
                          </p>
                          <p className="text-xs text-neutral-500">{user.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Mail className="w-4 h-4 text-neutral-400" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`user-actions-${user.user_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Change Role</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleRoleChange(user.user_id, 'homeowner')}
                            className="flex items-center gap-2"
                          >
                            <Home className="w-4 h-4" />
                            Homeowner
                            {user.role === 'homeowner' && <Check className="w-4 h-4 ml-auto text-green-500" />}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleRoleChange(user.user_id, 'sponsor')}
                            className="flex items-center gap-2"
                          >
                            <Briefcase className="w-4 h-4" />
                            Sponsor
                            {user.role === 'sponsor' && <Check className="w-4 h-4 ml-auto text-green-500" />}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleRoleChange(user.user_id, 'admin')}
                            className="flex items-center gap-2"
                          >
                            <Shield className="w-4 h-4" />
                            Admin
                            {user.role === 'admin' && <Check className="w-4 h-4 ml-auto text-green-500" />}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 bg-neutral-50">
            <p className="text-sm text-neutral-500">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} users
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-neutral-600">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Recent Signups Quick View */}
      {stats?.recent_signups?.length > 0 && (
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-500" />
            Most Recent Signups
          </h3>
          <div className="space-y-3">
            {stats.recent_signups.map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF3B30] to-[#FF6B6B] flex items-center justify-center text-white text-sm font-semibold">
                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900 text-sm">{user.name || 'Unnamed'}</p>
                    <p className="text-xs text-neutral-500">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  {getRoleBadge(user.role)}
                  <p className="text-xs text-neutral-400 mt-1">{formatDate(user.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
